<template>
  <div class="d-flex justify-center align-center pa-0 h-100">
    <div v-if="!githubRepo" class="d-flex align-center justify-center flex-column my-10 text-center">
      <p class="text-h5 mb-3">Connect to GitHub</p>
      <connect-github
        v-model:selectedAccount="selectedAccount"
        :selectedRepo="selectedRepo"
        :selectedBranch="selectedBranch"
        @update:selectedRepo="(repo) => emit('update:selectedRepo', repo)"
        @update:selectedBranch="(branch) => emit('update:selectedBranch', branch)"
        @onRepoCreated="(newRepo) => emit('onRepoCreated', selectedAccount.login, newRepo)"
        @onLinkRepo="() => linkGitHubRepo(selectedAccount.login, selectedRepo, selectedBranch)"
      />
    </div>
    <v-row v-else class="ma-0 h-100">
      <v-col sm="3" class="pa-0">
        <div class="d-flex flex-column ga-2 pa-2">
          <div class="d-flex">
            <span>Source Control</span>
            <v-spacer></v-spacer>
            <v-btn
              @click="doCommit()"
              :disabled="!Object.keys(diffObjects).length || !commitMessage.length || store.state.isWorkflowDisabled"
              size="small"
              icon="mdi-check"
            ></v-btn>
            <v-btn @click="refreshGithub()" size="small" icon="mdi-refresh"></v-btn>
            <v-menu>
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  size="small"
                  icon
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item @click="onUnlinkRepo" :disabled="store.state.isWorkflowDisabled">
                  <v-list-item-title>Unlink GitHub</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <v-tooltip location="bottom" open-delay="500">
            <template v-slot:activator="{ props: props }">
              <p v-bind="props" class="repo-name-ellipsis">
                Repository: <strong>{{ githubRepo }}</strong>
              </p>
            </template>
            <span>{{ githubRepo }}</span>
          </v-tooltip>
          <v-text-field
            v-model="commitMessage"
            variant="outlined"
            density="compact"
            single-line
            hide-details
          />
          <v-btn @click="doCommit()" :disabled="!Object.keys(diffObjects).length || !commitMessage.length || store.state.isWorkflowDisabled">
            <v-progress-circular v-if="isCommitting" indeterminate size="16" width="2" class="mr-1" />
            <v-icon v-else>mdi-check</v-icon> Commit & Push
          </v-btn>
        </div>
        <v-divider />
        <v-list
          style="height: 610px"
          bg-color="transparent"
          density="compact"
          open-strategy="multiple"
          v-model:selected="tabs"
          v-model:opened="opened"
        >
          <v-list-item
            v-for="(object, filepath) in diffObjects"
            :key="filepath"
            prepend-icon="mdi-file"
            :title="filepath.split('/').pop()"
            :value="filepath"
            :class="object.originalContents === null ? 'text-decoration-line-through' : ''"
          />
        </v-list>
        <v-divider />
        <div class="pa-2">
          <v-select
            :model-value="selectedBranch"
            :items="branches"
            label="Select Branch"
            item-title="name"
            item-value="name"
            variant="outlined"
            density="compact"
            style="width: 100%"
            @update:model-value="handleBranchChange"
          />
        </div>
      </v-col>
      <v-col sm="9" class="pa-0" style="background-color: var(--light-1); border-left: 1px solid var(--light-4);">
        <template v-for="(object, filename) in diffObjects" :key="filename">
          <div v-if="openedTab === filename" style="position: relative;">
            <div class="d-flex" style="border-bottom: 1px solid var(--light-4);">
              <span class="toolbar-top pl-3 pt-3">
                {{ object.filepath.replaceAll('/', ' > ')}}
                {{ object.originalContents === null ? '(deleted)' : '' }}
              </span>
              <v-spacer></v-spacer>
              <v-tooltip location="top" open-delay="500">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    style="border-radius: 0 !important;"
                    variant="text"
                    density="compact"
                    class="text-none"
                    :disabled="!object.isDirty || store.state.isWorkflowDisabled"
                    @click="saveContent(object)"
                  >
                    <v-icon class="mr-2">mdi-content-save</v-icon> Save
                  </v-btn>
                </template>
                <span>Save changes Ctrl/Cmd+S</span>
              </v-tooltip>
            </div>
            <code-diff-viewer
              v-if="!!object.originalContents"
              v-model:original="object.githubContents"
              v-model="object.userUpdatedContents"
              v-model:isDirty="object.isDirty"
              @save="saveContent(object)"
              :language="getFileExt(object.filepath)"
            />
            <code-editor
              v-else
              v-model="object.contents"
              v-model:isDirty="object.isDirty"
              @save="saveFile(object)"
              :language="getFileExt(object.filepath)"
            />
          </div>
        </template>
      </v-col>
      <v-dialog v-model="askBeforeSaveContent" max-width="480">
        <v-card title="Warning">
          <v-card-text>
            AI-generated changes were applied based on recent updates to the domain model. <br />
            Your own edits may conflict with these changes. How would you like to proceed?
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn @click="handleMerge">Merge</v-btn>
            <v-btn @click="handleReplace">Replace</v-btn>
            <v-btn @click="handleCancel">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { merge } from 'node-diff3'
import CodeEditor from '@/components/CodeEditor.vue'
import ConnectGithub from '@/components/ConnectGithub.vue'
import CodeDiffViewer from '@/components/CodeDiffViewer.vue'
import { getLanguageFromFilename } from '@/codegen/utils'

const {
  selectedRepo,
  selectedBranch,
  githubRepo,
  branches,
  makeCommit,
  diffObjects,
  fileObjects,
  saveFile,
  unlinkGitHub,
  refreshGithub
} = defineProps({
  selectedRepo: Object,
  selectedBranch: String,
  githubRepo: String,
  branches: Array,
  diffObjects: Object,
  fileObjects: Object,
  linkGitHubRepo: Function,
  makeCommit: Function,
  refreshGithub: Function,
  unlinkGitHub: Function,
  saveFile: Function
})

const emit = defineEmits(['update:selectedBranch', 'update:selectedRepo', 'onRepoCreated'])

const store = useStore()

const tabs = ref([])
const opened = ref([])
const openedTab = ref('')
const commitMessage = ref('')
const isCommitting = ref(false)
const askBeforeSaveContent = ref(false)
const selectedObject = ref({})
const selectedAccount = ref(null)

const onUnlinkRepo = () => {
  selectedAccount.value = null
  unlinkGitHub()
}

const getFileExt = (filePath) => {
  return getLanguageFromFilename(filePath)
}

const doCommit = async () => {
  if (isCommitting.value) return

  isCommitting.value = true
  await makeCommit(commitMessage.value)
  commitMessage.value = ''
  isCommitting.value = false
}

const saveContent = async (object) => {
  selectedObject.value = object

  if (object.aiUpdatedContents !== null) {
    askBeforeSaveContent.value = true
  } else {
    await handleReplace()
  }
}

const handleMerge = async () => {
  if (openedTab.value === '') return

  const baseLines = selectedObject.value.originalContents.split('\n')
  const aiUpdate = selectedObject.value.aiUpdatedContents.split('\n')
  const manualUpdate = selectedObject.value.userUpdatedContents.split('\n')

  const mergedResult = merge(aiUpdate, baseLines, manualUpdate)
  const hasConflict = mergedResult.conflict
  const mergedContents = mergedResult.result.join('\n')

  if (hasConflict) {
    store.commit('snackbar/showMessage', {
      content: 'Merge conflict detected. Please resolve manually.',
      timeout: 3000,
      color: 'red'
    })
  } else {
    store.commit('snackbar/showMessage', {
      content: 'User and AI updates merged successfully.',
      timeout: 3000,
      color: 'green'
    })
  }

  const updatedFileObject = {
    ...fileObjects[openedTab.value],
    contents: mergedContents,
    draftContents: null,
    isDirty: false
  }
  await saveFile(updatedFileObject)
  handleCancel()
}

const handleReplace = async () => {
  const updatedFileObject = {
    ...fileObjects[openedTab.value],
    contents: selectedObject.value.userUpdatedContents,
    draftContents: null,
    isDirty: false
  }
  await saveFile(updatedFileObject)
  handleCancel()
}

const handleCancel = () => {
  askBeforeSaveContent.value = false
  selectedObject.value = {}
}

const handleBranchChange = async (branch) => {
  emit('update:selectedBranch', branch)
}

onMounted(async () => {
  await refreshGithub()
})

watch(tabs, (val) => {
  if (val[0]) {
    openedTab.value = val[0]
  } else {
    if (!tabs.value.length && openedTab.value) {
      tabs.value = [openedTab.value]
    } else if (tabs.value.length === 0) {
      openedTab.value = ''
    }
  }
}, { deep: true })
</script>

<style scoped>
.repo-name-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
</style>
